"use client";

import cn from "@utils/cn";
import { usePathname } from "next/navigation";
import { useContext } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { UserContext } from "@/context/UserContext";
import { contactChannelTracking, contactUs, ctaClickTracking } from "@/dataLayer/dataLayer";
import { ContactServicesProps } from "@/types";

export default function ContactServices({ entry }: ContactServicesProps) {
  const { title, subtitle, cards } = entry;
  const { setGetInTouchModalOpen } = useContext(ModalsContext);
  const { setGenericRequestModalOpen, setinnovationLeadModalOpen } = useContext(ModalsContext);
  const { setIsDialogueOpen } = useContext(UserContext);
  const pathname = usePathname();
  const projectSlug = pathname?.split("/")?.find((segment, idx, arr) => arr[idx - 1] === "projects" || arr[idx - 1] === "communities");

  return (
    <div className="w-full">
      <article className="w-22\/24 md:w-20\/24 mx-auto py-l">
        {title && <h2 className="font-primary font-medium uppercase text-l">{title}</h2>}
        {subtitle && <h3 className="text-xs py-s">{subtitle}</h3>}

        <div className="flex flex-wrap gap-l mt-l">
          {cards.map((card, index) => {
            const multiCta = card.ctas?.length > 1;

            return (
              <div key={`cardservice-${index}`} className={`bg-gray-7 w-full p-l rounded ${multiCta ? "md:flex-[1_100%]" : "md:flex-1"}`}>
                <div className={cn("flex flex-row md:flex-col md:justify-between h-full", multiCta ? "flex-col md:flex-row" : "flex-col")}>
                  <div className="mb-xl">
                    <h3 className="font-primary text-m uppercase mb-l">{card.title}</h3>
                    <p>{card.text}</p>
                  </div>
                  <div
                    className={cn(
                      multiCta
                        ? "flex flex-col w-full md:w-fit md:flex-row h-fit gap-m items-center md:ml-auto rtl:md:mr-auto rtl:md:ml-0"
                        : "w-full",
                    )}
                  >
                    {card.ctas?.map((cta: any) => (
                      <Button
                        key={cta.id}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.MEDIUM}
                        theme={ButtonTheme.BLACK}
                        href={cta?.link?.url.includes("#") ? "" : cta.link.url}
                        customClass="flex w-full justify-center items-center whitespace-nowrap"
                        onClick={(e) => {
                          contactUs({ cta_name: cta.label, title_text: card.text, cta_location: entry.title });
                          if ((cta?.link?.url ?? "").indexOf("book-now") >= 0) {
                            e.preventDefault();
                            setGetInTouchModalOpen(true);
                          }
                          if ((cta?.link?.url).length == 1 && (cta?.link?.url).indexOf("@") == 0) {
                            e.preventDefault();
                            setGenericRequestModalOpen(true);
                          }
                          if ((cta?.link?.url ?? "").indexOf("start-chat") >= 0) {
                            e.preventDefault();
                            setIsDialogueOpen(true);
                          }
                          ctaClickTracking(cta.label, "ContactServices", "ContactServices");
                          contactChannelTracking(card.title.toLocaleLowerCase(), projectSlug || "");
                        }}
                      >
                        <>
                          {cta.link.icon && (
                            <span className="flex items-center mr-xs rtl:mr-0 rtl:ml-xs">
                              <Icon className="flex" name={cta.link.icon} size="16" title={cta.link.icon} />
                            </span>
                          )}
                          <span>{cta.label}</span>
                        </>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </div>
  );
}
